/**
 * Unified CRM Service
 * Handles fetching combined contacts and leads from the unified CRM API
 */

const CRM_BASE_URL = "https://api.crm.dalmlabs.com/api/v1";

// Types based on frontend-unified-crm-integration.md
export type UnifiedType = "contact" | "lead";

export interface UnifiedCrmRecord {
  id: string;
  resource_type: UnifiedType;
  title?: string | null;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  display_name: string | null;
  last_name: string | null;
  status: string | null;
  updated_at: string;
  details: {
    // Contact-specific fields
    external_contact_id?: string;
    tags?: string[];
    attributes?: Record<string, unknown>;
    // Lead-specific fields
    source?: string;
    score?: number;
    custom?: Record<string, unknown>;
    // Not populated yet (always null so far), but once the backend fills
    // these in, a lead's conversation can be looked up directly by id
    // instead of the phone-search fallback.
    conversation_id?: string | null;
    external_thread_id?: string | null;
  };
}

export interface UnifiedCrmSummary {
  total_leads: number;
  qualified_leads: number;
  hot_leads: number;
  warm_leads: number;
  cold_leads: number;
  tag_counts: Array<{ tag: string; count: number }>;
}

export interface UnifiedCrmResponse {
  results: UnifiedCrmRecord[];
  page: number;
  per_page: number;
  total: number;
  counts: {
    contacts: number;
    leads: number;
    prospects?: number;
  };
  // Real backend-computed aggregates across ALL leads (not capped by
  // page/per_page) — only present on the leads-type unified response so far.
  summary?: UnifiedCrmSummary;
}

export interface UnifiedCrmParams {
  page?: number;
  per_page?: number;
  sort_order?: "asc" | "desc";
  search?: string;
  q?: string;
  type?: "contact" | "lead" | "contacts" | "leads";
  email?: string;
  phone?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
  email_contains?: string;
  phone_contains?: string;
  first_name_contains?: string;
  last_name_contains?: string;
  status_contains?: string;
}

export interface FetchUnifiedCrmResponse {
  success: boolean;
  data: UnifiedCrmResponse | null;
  message?: string;
}

/**
 * Get auth token from localStorage
 */
const getAuthToken = (): string | null => {
  return localStorage.getItem("authToken");
};

/**
 * Build query string from params, filtering out undefined values
 */
const buildQueryString = (params: UnifiedCrmParams): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

/**
 * Fetch unified CRM data (contacts and/or leads)
 */
export const fetchUnifiedCrm = async (
  params: UnifiedCrmParams = {},
): Promise<FetchUnifiedCrmResponse> => {
  const token = getAuthToken();

  if (!token) {
    return {
      success: false,
      data: null,
      message: "Not authenticated",
    };
  }

  // Set defaults
  const queryParams: UnifiedCrmParams = {
    page: 1,
    per_page: 100,
    sort_order: "desc",
    ...params,
  };

  try {
    const queryString = buildQueryString(queryParams);
    const url = `${CRM_BASE_URL}/crm/unified?${queryString}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[fetchUnifiedCrm] Error:", {
        status: response.status,
        errorText,
      });
      return {
        success: false,
        data: null,
        message: `API Error: ${response.status} - ${errorText}`,
      };
    }

    const data: UnifiedCrmResponse = await response.json();

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error("[fetchUnifiedCrm] Error:", error);
    return {
      success: false,
      data: null,
      message:
        error instanceof Error
          ? error.message
          : "Failed to fetch unified CRM data",
    };
  }
};

/**
 * Fetch all records (contacts + leads) - for CDP page
 */
export const fetchAllUnified = async (
  page: number = 1,
  perPage: number = 100,
  search?: string,
): Promise<FetchUnifiedCrmResponse> => {
  return fetchUnifiedCrm({
    page,
    per_page: perPage,
    sort_order: "desc",
    ...(search && { search }),
  });
};

/**
 * Fetch only leads - for Leads page
 */
export const fetchLeadsUnified = async (
  page: number = 1,
  perPage: number = 100,
  search?: string,
): Promise<FetchUnifiedCrmResponse> => {
  return fetchUnifiedCrm({
    page,
    per_page: perPage,
    sort_order: "desc",
    type: "lead",
    ...(search && { search }),
  });
};

/**
 * Fetch only contacts - for Contacts page (if needed)
 */
export const fetchContactsUnified = async (
  page: number = 1,
  perPage: number = 100,
  search?: string,
): Promise<FetchUnifiedCrmResponse> => {
  return fetchUnifiedCrm({
    page,
    per_page: perPage,
    sort_order: "desc",
    type: "contact",
    ...(search && { search }),
  });
};

/**
 * Helper to get full name from record
 */
export const getFullName = (record: UnifiedCrmRecord): string => {
  const firstName = record.first_name || "";
  const lastName = record.last_name || "";
  return [firstName, lastName].filter(Boolean).join(" ").trim() || "N/A";
};

// `display_name` is a real field but the backend isn't populating it yet
// (always null so far) — fall back to first/last name rather than showing
// "N/A" for every single lead until the backend starts sending it.
export const getDisplayName = (record: UnifiedCrmRecord): string => {
  const displayName = (record.display_name || "").trim();
  return displayName || getFullName(record);
};

/**
 * Helper to get initials from record
 */
export const getInitials = (record: UnifiedCrmRecord): string => {
  if (record.display_name?.trim()) {
    const names = record.display_name.trim().split(/\s+/);

    const first = names[0]?.charAt(0).toUpperCase() || "";
    const last =
      names.length > 1 ? names[names.length - 1].charAt(0).toUpperCase() : "";

    return (first + last) || "??";
  }

  const first = record.first_name?.charAt(0).toUpperCase() || "";
  const last = record.last_name?.charAt(0).toUpperCase() || "";

  return (first + last) || "??";
};
